import { useState } from 'react';
import { SwitchCase } from '@toss/react';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import '../events.quiz-image-upload';
import { trackEvent } from '@zep/logger/tracking';
import { BetaChip } from '@/shared/components/Chip/BetaChip';
import { SearchTabScreen } from '@/shared/features/image-search/components';
import type { ImageUploadModalPlace } from '@/shared/features/image-search/types';
import { useCountryCode } from '@/shared/hooks';
import { DialogBody, DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { TabsList, TabsRoot, TabsTrigger } from '@/ui_v3/components/tabs';
import { cn } from '@/utils';
import { UploadTabScreen } from './UploadTabScreen';
interface ImageUploadModalProps {
  onClose: () => void;
  onUploadStart: (files: File[]) => void;
  place: ImageUploadModalPlace;
  imageUrl?: string;
  multiple?: boolean;
}
export type ImageUploadTabType = 'upload' | 'naver' | 'pexels';
const imageTabTriggerProps = {
  size: 'md',
  className: 'hover:bg-opacity-black-1 data-[state=inactive]:hover:rounded-xs'
} as const;
export const ImageUploadModal = ({
  onClose,
  onUploadStart,
  place,
  imageUrl,
  multiple = false
}: ImageUploadModalProps) => {
  const [screen, setScreen] = useState<ImageUploadTabType>('upload');
  const {
    t
  } = useTranslation(['build']);
  const {
    isKR
  } = useCountryCode();
  const isNaverEnabled = isKR;
  const handleTabChange = (value: ImageUploadTabType) => {
    if (value === 'naver' || value === 'pexels') {
      trackEvent('image_search_tab_click', {
        provider: value,
        place
      });
    }
    setScreen(value);
  };
  return <ResponsiveDialog size={'2xl'} onClose={onClose} shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="ImageUploadModal" data-sentry-source-file="ImageUploadModal.tsx">
      <DialogHeader title={t('image.addImage')} hasCloseButton={true} onClose={onClose} data-sentry-element="DialogHeader" data-sentry-source-file="ImageUploadModal.tsx" />
      <DialogBody autoFocus={false} onMouseDown={e => {
      e.stopPropagation();
    }} onClick={e => {
      e.stopPropagation();
    }} className={cn('flex flex-col px-md mobile-lg:px-lg h-[644px] relative', screen === 'upload' && 'pb-lg')} data-sentry-element="DialogBody" data-sentry-source-file="ImageUploadModal.tsx">
        <div className="pb-md">
          <TabsRoot value={screen} onValueChange={v => handleTabChange(v as ImageUploadTabType)} data-sentry-element="TabsRoot" data-sentry-source-file="ImageUploadModal.tsx">
            <TabsList className="w-full" data-sentry-element="TabsList" data-sentry-source-file="ImageUploadModal.tsx">
              <TabsTrigger value="upload" {...imageTabTriggerProps} data-sentry-element="TabsTrigger" data-sentry-source-file="ImageUploadModal.tsx">
                {t('image.uploadTab.title')}
              </TabsTrigger>
              {isNaverEnabled && <TabsTrigger value="naver" {...imageTabTriggerProps}>
                  <div className="flex items-center gap-3xs">
                    {t('image.naverTab.title')}
                    <BetaChip variant="outlined" className="border-yellow-600 text-yellow-900" />
                  </div>
                </TabsTrigger>}
              <TabsTrigger value="pexels" {...imageTabTriggerProps} data-sentry-element="TabsTrigger" data-sentry-source-file="ImageUploadModal.tsx">
                {isNaverEnabled ? t('image.pexelsTab.title') : t('image.searchTab.title')}
              </TabsTrigger>
            </TabsList>
          </TabsRoot>
        </div>
        <div className="flex-1 overflow-y-auto">
          <SwitchCase value={screen} caseBy={{
          upload: <UploadTabScreen onUploadStart={onUploadStart} place={place} imageUrl={imageUrl} multiple={multiple} />,
          ...(isNaverEnabled ? {
            naver: <SearchTabScreen key="naver" provider="naver" place={place} onUploadStart={onUploadStart} imageUrl={imageUrl} />
          } : {}),
          pexels: <SearchTabScreen key="pexels" provider="pexels" place={place} onUploadStart={onUploadStart} imageUrl={imageUrl} multiple={multiple} />
        }} data-sentry-element="SwitchCase" data-sentry-source-file="ImageUploadModal.tsx" />
        </div>
      </DialogBody>
    </ResponsiveDialog>;
};
interface SingleImageUploadModalOptions {
  multiple?: false;
  onUploadStart: (file: File) => void;
  place: ImageUploadModalPlace;
  imageUrl?: string;
}
interface MultipleImageUploadModalOptions {
  multiple: true;
  onUploadStart: (files: File[]) => void;
  place: ImageUploadModalPlace;
  imageUrl?: never;
}
type ImageUploadModalOptions = SingleImageUploadModalOptions | MultipleImageUploadModalOptions;
export const showImageUploadModal = (options: ImageUploadModalOptions) => {
  return overlay.open(({
    unmount
  }) => {
    return <ImageUploadModal onClose={unmount} onUploadStart={files => {
      const firstFile = files[0];
      if (!firstFile) return;
      if (options.multiple) {
        options.onUploadStart(files);
      } else {
        options.onUploadStart(firstFile);
      }
      unmount();
    }} place={options.place} imageUrl={options.imageUrl} multiple={options.multiple} />;
  });
};